import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, Type } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { CustomErrorStateMatcher } from '../../utils/custom-error-state-matcher';

export interface TextareaProps extends FormlyFieldProps {
  autosize?: boolean;
  autosizeMinRows?: number;
  autosizeMaxRows?: number;
  maxRows?: number;
  maxLineLength?: number;
  pattern?: string | RegExp;
  hint?: string;
}

export interface FormlyTextareaFieldConfig extends FormlyFieldConfig<TextareaProps> {
  type: 'textarea' | Type<TextareaComponent>;
}

@Component({
  selector: 'vectronic-formly-textarea',
  imports: [CommonModule, MatInputModule, ReactiveFormsModule, FormlyModule, MatFormFieldModule],
  template: `
    <mat-form-field>
      <mat-label>{{ props.label }}</mat-label>
      <textarea
        #textarea
        matInput
        [id]="id"
        [name]="field.name"
        [readonly]="props.readonly"
        [required]="props?.required"
        [formControl]="formControl"
        [errorStateMatcher]="matcher"
        [cols]="props.cols"
        [rows]="props.rows"
        [formlyAttributes]="field"
        [placeholder]="props.placeholder"
        [tabindex]="props.tabindex"
        [cdkTextareaAutosize]="props.autosize"
        [cdkAutosizeMinRows]="props.autosizeMinRows"
        [cdkAutosizeMaxRows]="props.autosizeMaxRows"
        [class.cdk-textarea-autosize]="props.autosize"
      ></textarea>
      @if (props?.hint) {
        <mat-hint align="start">{{ props.hint }}</mat-hint>
      }
      @if (props?.maxLength) {
        <mat-hint align="end"
          ><span [ngStyle]="{ color: textarea.value.length < props.maxLength ? 'inherit' : 'red' }"
            >{{ textarea.value.length }} / {{ props.maxLength }}</span
          ></mat-hint
        >
      }
      <mat-error>
        <formly-validation-message [field]="field"></formly-validation-message>
      </mat-error>
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    mat-form-field {
      display: block;
    }
  `,
})
export class TextareaComponent extends FieldType<FieldTypeConfig<TextareaProps>> implements OnInit {
  matcher = new CustomErrorStateMatcher();

  ngOnInit(): void {
    if (this.props?.maxRows) {
      this.formControl.addValidators(this.maxRowsValidator(this.props.maxRows));
    }
    if (this.props?.maxLineLength) {
      this.formControl.addValidators(this.maxLineLengthValidator(this.props.maxLineLength));
    }
  }
  maxRowsValidator(maxRows: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';
      const rows = value.split('\n');
      return rows.length <= maxRows
        ? null
        : { maxRows: { message: `The input exceeds the maximum number of ${maxRows} rows` } };
    };
  }

  maxLineLengthValidator(maxLineLength: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value || '') as string;
      const rows = value.split('\n');
      const invalidLine = rows.some((row) => row.length > maxLineLength);
      return !invalidLine
        ? null
        : { maxLineLength: { message: `Each line must be ${maxLineLength} characters or fewer` } };
    };
  }
}
